/**
 * Site Config Composable
 * 管理網站配置
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/siteConfig.js)
 * - API：使用 useFetch 支援 SSR
 * - 單例模式：使用 nuxtApp._siteContext 避免 watch/storeToRefs 重複創建
 */

import { decode } from 'js-base64'
import { storeToRefs } from 'pinia'
import { getCurrentInstance, onMounted, watch } from 'vue'

import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { commonApi } from '~/api/common'
import { useSiteConfigStore } from '~/stores/siteConfig'
import { storage } from '@/utils/storage'

export const useSite = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 watch 和 storeToRefs 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._siteContext) {
    const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public
    const store = useSiteConfigStore()

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 從 commonApi 取得 API 配置
    const apiConfig = commonApi.getConfig({ appSecret })
    const apiPath = apiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 注意：移除 onResponse/onResponseError 閉包，改用 watch 處理資料更新
    // 這樣可以避免 SSR 環境下閉包累積導致的記憶體洩漏
    const { data, error, refresh } = useFetch(`${baseUrl}/${apiPath}`, {
      key: 'site-config',
      dedupe: 'defer', // 如果有相同 key 請求進行中，等待它完成而不是發新請求
      query: apiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
    })

    // 使用 watch 代替 onResponse，避免閉包累積
    // watch 只創建一次
    watch(
      data,
      (newData) => {
        if (newData?.config) {
          const config = JSON.parse(decode(newData.config))
          store.setSiteConfig(config)
        }
      },
      { immediate: true }
    )

    // 使用 watch 代替 onResponseError，避免閉包累積
    // watch 只創建一次
    watch(error, (err) => {
      if (err) {
        console.error('[useSite] fetchSiteConfig API error:', err)
        store.setSiteConfig({})
      }
    })

    // storeToRefs 只調用一次
    nuxtApp._siteContext = {
      ...storeToRefs(store),
      fetchSiteConfig: refresh,
    }
  }

  // Client 端 mounted 後存儲 siteConfig 到 localStorage
  // 只有在組件上下文中才註冊 onMounted，避免在 plugin/middleware 中調用時出現警告
  if (getCurrentInstance()) {
    const store = useSiteConfigStore()
    onMounted(() => {
      if (store.siteConfig) {
        storage.set('site-config', store.siteConfig)
      }
    })
  }

  return nuxtApp._siteContext
}
